import React, { useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Ban, Copy, Home, Search, ShieldX, X } from "lucide-react";
import { GracePicker, graceLabel } from "@/components/blacklist/GracePicker";

export const guildIconUrl = (g) => (g.icon ? `https://cdn.discordapp.com/icons/${g.id}/${g.icon}.${g.icon.startsWith("a_") ? "gif" : "png"}?size=64` : null);

// Current list ∪ last full Discord-login list; servers only in the older full list are tagged `stale`.
export const mergeGuilds = (current = [], full = []) => {
  const map = new Map((full || []).map((g) => [g.id, { ...g, stale: true }]));
  (current || []).forEach((g) => map.set(g.id, { ...(map.get(g.id) || {}), ...g, stale: false }));
  return [...map.values()];
};

function GuildIcon({ g }) {
  const [broken, setBroken] = useState(false);
  const url = guildIconUrl(g);
  if (url && !broken) return <img src={url} alt="" onError={() => setBroken(true)} className="w-7 h-7 rounded-full shrink-0 object-cover" />;
  const initials = (g.name || "?").split(/\s+/).map((w) => w[0]).join("").slice(0, 2).toUpperCase();
  return <div className="w-7 h-7 rounded-full shrink-0 bg-[var(--ff-surface-hover)] text-[0.55rem] flex items-center justify-center text-[var(--ff-text-dim)]">{initials}</div>;
}

function BlacklistGuildDialog({ guild, ownerName, defaultGrace, onClose, onDone }) {
  const [reason, setReason] = useState("");
  const [grace, setGrace] = useState(defaultGrace);
  const [saving, setSaving] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.post("/admin/blacklist", {
        guild_id: guild.id, name: guild.name || null,
        reason: reason.trim() || `Flagged from ${ownerName || "a user"}'s profile`, grace_hours: grace,
      });
      toast.success(`Blacklisted ${guild.name || guild.id} • ${graceLabel(data.blacklist.grace_hours)} to leave • ${data.scan.newly_banned} newly banned of ${data.scan.scanned} scanned`);
      onDone(data);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" onClick={() => !saving && onClose()} data-testid="blacklist-guild-dialog">
      <form onSubmit={submit} onClick={(e) => e.stopPropagation()} className="surface-card p-6 w-full max-w-md space-y-4">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <GuildIcon g={guild} />
            <div className="min-w-0">
              <div className="label-eyebrow text-[var(--ff-red)]">▮ BLACKLIST SERVER</div>
              <div className="font-display text-xl truncate">{guild.name || "Unnamed server"}</div>
              <div className="font-mono-ui text-[0.65rem] text-[var(--ff-text-dim)] break-all" data-testid="blacklist-guild-dialog-id">{guild.id}</div>
            </div>
          </div>
          <button type="button" onClick={onClose} className="text-[var(--ff-text-dim)] hover:text-white" data-testid="blacklist-guild-cancel-x"><X size={18} /></button>
        </div>
        <p className="text-xs text-[var(--ff-text-dim)]">Everyone we find in this server will be warned and auto-banned once their time to leave runs out (or right away if you pick &quot;Ban immediately&quot;). A full rescan runs now.</p>
        {guild.stale && (
          <p className="text-xs text-amber-300 border border-amber-300/30 px-3 py-2" data-testid="blacklist-guild-stale-note">
            This server is from {ownerName || "this user"}&apos;s last full Discord login and hasn&apos;t been re-confirmed, so they won&apos;t be caught until they sign in with Discord again. Other members we can currently see in it are caught right away.
          </p>
        )}
        <input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason (optional)" maxLength={200} className="w-full px-3 py-2.5 text-sm" data-testid="blacklist-guild-reason" />
        <GracePicker value={grace} onChange={setGrace} testId="blacklist-guild-grace" />
        <div className="flex justify-end gap-2 pt-1">
          <button type="button" onClick={onClose} className="btn-ghost text-xs" data-testid="blacklist-guild-cancel">Cancel</button>
          <button disabled={saving} className="btn-primary text-xs" data-testid="blacklist-guild-confirm"><ShieldX size={12} /> {saving ? "Blacklisting…" : "Blacklist & scan"}</button>
        </div>
      </form>
    </div>
  );
}

function GuildRow({ g, isBad, isHome, onBlacklist, testPrefix }) {
  const copy = () => navigator.clipboard?.writeText(g.id).then(() => toast.success("Server ID copied")).catch(() => {});
  return (
    <li className={`px-3 py-2 border flex items-center gap-3 ${isBad ? "border-[var(--ff-red)] bg-[var(--ff-red)]/10" : "border-[var(--ff-border)]"}`} data-testid={`${testPrefix}-guild-${g.id}`}>
      <GuildIcon g={g} />
      <div className="min-w-0 flex-1">
        <div className={`text-sm truncate ${isBad ? "text-[var(--ff-red)]" : ""}`}>
          {g.name || "Unnamed server"}
          {g.stale && <span className="ml-2 text-[0.55rem] font-mono-ui uppercase tracking-[0.12em] text-[var(--ff-text-dim)]" title="From their last full Discord login — not re-confirmed since" data-testid={`${testPrefix}-guild-stale-${g.id}`}>seen at last login</span>}
        </div>
        <button type="button" onClick={copy} className="font-mono-ui text-[0.6rem] text-[var(--ff-text-dim)] hover:text-white inline-flex items-center gap-1" title="Copy server ID" data-testid={`${testPrefix}-guild-copy-${g.id}`}>
          {g.id} <Copy size={9} />
        </button>
      </div>
      {isHome ? (
        <span className="text-[0.55rem] font-mono-ui uppercase tracking-[0.15em] text-emerald-400 inline-flex items-center gap-1 shrink-0" data-testid={`${testPrefix}-guild-home-${g.id}`}><Home size={10} /> Our server</span>
      ) : isBad ? (
        <span className="text-[0.55rem] font-mono-ui uppercase tracking-[0.15em] text-[var(--ff-red)] inline-flex items-center gap-1 shrink-0" data-testid={`${testPrefix}-guild-blacklisted-${g.id}`}><Ban size={10} /> Blacklisted</span>
      ) : (
        <button type="button" onClick={() => onBlacklist(g)} style={{ color: "var(--ff-red)" }} className="btn-ghost inline-flex items-center gap-1 text-[0.6rem] shrink-0 border-[var(--ff-red)]/40 hover:bg-[var(--ff-red)]/10" data-testid={`${testPrefix}-blacklist-btn-${g.id}`}>
          <ShieldX size={11} /> Blacklist Server
        </button>
      )}
    </li>
  );
}

// A user's Discord servers (from the OAuth `guilds` scope) with a per-server Blacklist button.
export default function UserServers({ guilds: current = [], fullGuilds = [], fullAt, blacklistIds, homeGuildId, defaultGrace = 24, ownerName, onBlacklisted, syncedAt, syncSource, testPrefix = "user" }) {
  const [q, setQ] = useState("");
  const [target, setTarget] = useState(null);
  const guilds = useMemo(() => mergeGuilds(current, fullGuilds), [current, fullGuilds]);
  const sorted = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return [...guilds]
      .filter((g) => !needle || (g.name || "").toLowerCase().includes(needle) || g.id.includes(needle))
      .sort((a, b) => (blacklistIds.has(b.id) - blacklistIds.has(a.id)) || (a.name || "").localeCompare(b.name || ""));
  }, [guilds, q, blacklistIds]);

  if (guilds.length === 0) {
    return <p className="text-xs text-[var(--ff-text-dim)] font-mono-ui" data-testid={`${testPrefix}-servers-empty`}>No servers on file — the user needs to sign in with Discord again to share their server list.</p>;
  }
  return (
    <div className="space-y-2" data-testid={`${testPrefix}-servers`}>
      <div className="flex flex-wrap items-center gap-3">
        <div className="label-eyebrow">▮ DISCORD SERVERS · {guilds.length}</div>
        {syncedAt && <span className="text-[0.6rem] font-mono-ui text-[var(--ff-text-dim)]">synced {new Date(syncedAt).toLocaleString()}{syncSource ? ` · via ${syncSource === "oauth" ? "Discord login" : syncSource === "bot_probe" ? "bot check (partial)" : syncSource}` : ""}</span>}
        {fullAt && syncSource !== "oauth" && <span className="text-[0.6rem] font-mono-ui text-amber-300" data-testid={`${testPrefix}-servers-full-at`}>full list from Discord login {new Date(fullAt).toLocaleDateString()}</span>}
        {guilds.length > 8 && (
          <div className="relative ml-auto">
            <Search size={11} className="absolute left-2 top-1/2 -translate-y-1/2 text-[var(--ff-text-dim)]" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Filter servers…" className="pl-7 pr-2 py-1 text-xs w-48" data-testid={`${testPrefix}-servers-filter`} />
          </div>
        )}
      </div>
      <ul className="grid md:grid-cols-2 gap-1.5">
        {sorted.map((g) => (
          <GuildRow key={g.id} g={g} isBad={blacklistIds.has(g.id)} isHome={g.id === homeGuildId} onBlacklist={setTarget} testPrefix={testPrefix} />
        ))}
      </ul>
      {target && (
        <BlacklistGuildDialog guild={target} ownerName={ownerName} defaultGrace={defaultGrace}
          onClose={() => setTarget(null)} onDone={(data) => { setTarget(null); onBlacklisted?.(data); }} />
      )}
    </div>
  );
}
